<script setup lang="ts">
/**
 * Assistant CRUD panel.
 *
 * Responsibilities — keep it boring:
 *   - List all assistants (incl. disabled) on mount
 *   - Edit one in a side form, save = PUT, soft-delete = DELETE → reload
 *   - Show the audit context inline (this assistant's last upsert/disable)
 *
 * No router; selection is local state. All API calls go through `api/admin`.
 *
 * One editor per assistant (2026-09-24 review). The Governance tab used to
 * have a second drawer for the same assistant, with its own Save and half
 * the same facts; its editing moved here. The editor has four sections —
 * Setup, Who can use it, What it reads, Compliance — and one Save that
 * writes whichever of them changed. `#/admin?tab=assistants&assistant=x&
 * section=compliance` opens straight onto one, which is how the to-do list
 * and the Compliance report link in.
 */
import { vWrite } from "../admin-read-only";
import { computed, onMounted, onUnmounted, ref } from "vue";
import {
  AdminError,
  type AssistantRow,
  type GlossaryEntryRow,
  type GovernanceAssistant,
  type GovernanceResponse,
  type ReadsSummary,
  type ScopeTagInfo,
  disableAssistant,
  getAssistantsReadsSummary,
  getGovernance,
  getScopeTags,
  getTeams,
  listAssistants,
  listGlossary,
  upsertAssistant,
} from "../api/admin";
import { adminParam, groupLabel } from "../utils/vocabulary";
import AccessTests from "./AccessTests.vue";
import AssistantAccessReview from "./AssistantAccessReview.vue";
import AssistantCompliance from "./AssistantCompliance.vue";
import { useToast } from "../composables/useToast";
import { useConfirm } from "../composables/useConfirm";
import AssistantMembers from "./AssistantMembers.vue";
import AssistantReads from "./AssistantReads.vue";
import RefineAssistant from "./RefineAssistant.vue";
import ScopeTagPill from "./ScopeTagPill.vue";
import SidePanel from "./SidePanel.vue";
import SkeletonRows from "./SkeletonRows.vue";

const toast = useToast();
const { confirm } = useConfirm();
// Per-row in-flight guard so Disable/Enable can't be double-clicked.
const busySlugs = ref<Set<string>>(new Set());

const props = defineProps<{ devUser: string }>();

const rows = ref<AssistantRow[]>([]);
const loading = ref(false);
const editing = ref<AssistantRow | null>(null);
const aclScopeText = ref("");
const contentScopeText = ref("");
const settingsText = ref("");

// Folded sections of the editor. Reset on every open: a fold left open on
// one assistant should not greet you on the next.
const refineOpen = ref(false);
const glossaryOpen = ref(false);
const advancedOpen = ref(false);

/** What the Advanced fold is currently set to, so it need not be opened to
 *  find out. Only what differs from the defaults is worth a word. */
const advancedSummary = computed(() => {
  const e = editing.value;
  if (!e) return "";
  const parts: string[] = [];
  const scope = contentScopeText.value.trim();
  parts.push(scope ? `content scope: ${scope}` : "no content scope");
  if (e.chat_deployment) parts.push(`model: ${e.chat_deployment}`);
  if (e.dedicated_index) parts.push("own index");
  return parts.join(" · ");
});
const saving = ref(false);

// ADR 0026 v2 — the glossary library is loaded once and rendered as a
// multi-select so operators don't have to hand-edit
// `settings.glossary_includes` in the raw JSON box.
const glossaryEntries = ref<GlossaryEntryRow[]>([]);
const glossaryIncludes = ref<Set<string>>(new Set());
/** settings.access_mode === "listed": the group door is shut, only teams,
 * named people, owners and pilot users get in. The control wins over the
 * raw JSON, like the glossary picker. */
const accessListed = ref(false);

/**
 * Filter over slug, display name and both scope lists. Fifteen assistants is
 * already past the point where scanning the table beats typing three letters,
 * and this list only grows.
 */
const query = ref("");
const filtered = computed<AssistantRow[]>(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return rows.value;
  return rows.value.filter((r) =>
    [r.slug, r.name, ...(r.acl_scope ?? []), ...(r.content_scope ?? [])]
      .join(" ")
      .toLowerCase()
      .includes(q),
  );
});

/** Drawer open state, derived from `editing` so one source of truth stays. */
const editorOpen = computed({
  get: () => editing.value !== null,
  set: (v: boolean) => {
    if (!v) cancelEdit();
  },
});

function _toastError(e: unknown, context: string): void {
  const msg = e instanceof AdminError ? e.message : String(e);
  toast.error(context, msg);
}

async function reload() {
  loading.value = true;
  try {
    rows.value = await listAssistants(props.devUser, false);
  } catch (e) {
    _toastError(e, "Could not load assistants");
  } finally {
    loading.value = false;
  }
}

/** Re-read the rows without the skeleton — after a compliance change, so
 *  a later Save starts from what the server now holds. */
async function refreshRows(): Promise<void> {
  try {
    rows.value = await listAssistants(props.devUser, false);
  } catch {
    // The next full reload will say so; this one is housekeeping.
  }
}

// ── Compliance (moved here from the Governance tab's drawer) ───────────────

const gov = ref<GovernanceResponse | null>(null);
const govBySlug = computed(
  () => new Map((gov.value?.assistants ?? []).map((a) => [a.slug, a])),
);

async function loadGov(): Promise<void> {
  try {
    gov.value = await getGovernance(props.devUser);
  } catch {
    gov.value = null;
  }
}

function onGovUpdated(a: GovernanceAssistant): void {
  if (gov.value)
    gov.value.assistants = gov.value.assistants.map((x) =>
      x.slug === a.slug ? a : x,
    );
  void refreshRows();
}

/** The three steps before use (ADR 0066), as the Approvals page shows
 * them: one status per assistant, the same marks everywhere. */
function steps(slug: string) {
  return govBySlug.value.get(slug)?.readiness?.steps ?? [];
}
function openItems(slug: string): number {
  return steps(slug).filter((st) => st.status === "todo").length;
}
const MARK = { ok: "✓", todo: "✗", na: "–" } as const;

type Section = "setup" | "access" | "reads" | "compliance";
const SECTIONS: { id: Section; label: string }[] = [
  { id: "setup", label: "Setup" },
  { id: "access", label: "Who can use it" },
  { id: "reads", label: "What it reads" },
  { id: "compliance", label: "Approval" },
];
const section = ref<Section>("setup");
const compliance = ref<InstanceType<typeof AssistantCompliance> | null>(null);
const complianceDirty = computed(() => !!compliance.value?.dirty);

// ── Teams (ADR 0063) ──────────────────────────────────────────────────────

/** Teams moved to their own sub-tab (Admin → Assistants → Teams); the
 *  "Manage teams" link goes there. `tab=assistants&view=teams` still lands
 *  on it — AdminPage maps the old link. */
function openTeams(): void {
  editing.value = null;
  window.location.hash = "#/admin?tab=teams";
}

/** slug → names of teams that may use it / that it was made for. */
const teamAccess = ref<Map<string, string[]>>(new Map());
const teamMadeFor = ref<Map<string, string[]>>(new Map());
async function loadTeams(): Promise<void> {
  try {
    const { teams } = await getTeams(props.devUser);
    const access = new Map<string, string[]>();
    const madeFor = new Map<string, string[]>();
    for (const t of teams) {
      for (const a of t.access)
        access.set(a, [...(access.get(a) ?? []), t.name]);
      for (const a of t.made_for)
        madeFor.set(a, [...(madeFor.get(a) ?? []), t.name]);
    }
    teamAccess.value = access;
    teamMadeFor.value = madeFor;
  } catch {
    // Non-fatal: the list still renders without team names.
  }
  void loadGov();
}

/** `assistant=<slug>&section=<id>` in the hash opens that editor. */
function openFromHash(): void {
  const slug = adminParam("assistant");
  const row = rows.value.find((r) => r.slug === slug);
  if (!row) return;
  const want = adminParam("section") as Section;
  startEdit(row, SECTIONS.some((x) => x.id === want) ? want : "setup");
}

/** Tag -> what it means, derived from the registered sources. Fetched once
 * per mount so hovering a pill is instant and does not fan out a request
 * per row. Non-fatal: without it the pills still render, just unannotated. */
const scopeTags = ref<Map<string, ScopeTagInfo>>(new Map());

async function loadScopeTags(): Promise<void> {
  try {
    const tags = await getScopeTags(props.devUser);
    scopeTags.value = new Map(tags.map((t) => [t.tag, t]));
  } catch {
    scopeTags.value = new Map();
  }
}

/** What each assistant reads BEYOND its scope tags — the fact tables it can
 * query and the tools it can call (ADR 0059). The scope-tag pills cover the
 * search index; without this, an assistant that queries CRM, Business Central
 * and eRegnskab reads as "contracts" on this page, and the only way to find
 * out otherwise is to open it. Fetched once per mount, in bulk.
 *
 * Non-fatal: the table still renders without it. */
const reads = ref<Record<string, ReadsSummary>>({});

async function loadReads(): Promise<void> {
  try {
    reads.value = await getAssistantsReadsSummary(props.devUser);
  } catch {
    reads.value = {};
  }
}

async function loadGlossary(): Promise<void> {
  try {
    glossaryEntries.value = await listGlossary(props.devUser);
  } catch (e) {
    // Non-fatal — admin can still edit assistants if the glossary
    // endpoint is misbehaving; they just won't see the picker.
    glossaryEntries.value = [];
    _toastError(e, "Could not load glossary library");
  }
}

onMounted(async () => {
  void loadGlossary();
  void loadScopeTags();
  void loadReads();
  void loadGov();
  void loadTeams();
  await reload();
  openFromHash();
  window.addEventListener("hashchange", openFromHash);
});
onUnmounted(() => window.removeEventListener("hashchange", openFromHash));

function _readGlossaryIncludes(settings: unknown): Set<string> {
  const obj = settings as Record<string, unknown> | null | undefined;
  const raw =
    obj && Array.isArray(obj.glossary_includes) ? obj.glossary_includes : [];
  return new Set(raw.filter((x): x is string => typeof x === "string"));
}

function toggleGlossaryInclude(slug: string): void {
  const next = new Set(glossaryIncludes.value);
  if (next.has(slug)) next.delete(slug);
  else next.add(slug);
  glossaryIncludes.value = next;
}

function startNew() {
  editing.value = {
    slug: "",
    name: "",
    description: "",
    system_prompt: "",
    chat_deployment: null,
    acl_scope: [],
    content_scope: [],
    dedicated_index: false,
    settings: {},
    enabled: true,
    // ADR 0027 — the backend's default is `production` (it serves the seed
    // path), so a new assistant must say otherwise or it goes live to its
    // whole group the moment it is saved.
    maturity: "experimental",
  };
  aclScopeText.value = "";
  contentScopeText.value = "";
  settingsText.value = "{}";
  glossaryIncludes.value = new Set();
  accessListed.value = false;
  refineOpen.value = false;
  glossaryOpen.value = false;
  // A new assistant needs its content scope chosen; show where that is.
  advancedOpen.value = true;
  section.value = "setup";
  snapshot.value = formState();
  settingsOriginal.value = settingsText.value;
}

function startEdit(row: AssistantRow, at: Section = "setup") {
  // Backwards-compat: older backend responses may omit
  // content_scope/dedicated_index; default them so the editor
  // can still bind v-model without crashing.
  const safe: AssistantRow = {
    ...row,
    content_scope: row.content_scope ?? [],
    dedicated_index: row.dedicated_index ?? false,
  };
  editing.value = JSON.parse(JSON.stringify(safe));
  aclScopeText.value = safe.acl_scope.join(", ");
  contentScopeText.value = safe.content_scope.join(", ");
  settingsText.value = JSON.stringify(row.settings, null, 2);
  glossaryIncludes.value = _readGlossaryIncludes(row.settings);
  accessListed.value =
    (row.settings as Record<string, unknown> | undefined)?.access_mode === "listed";
  refineOpen.value = false;
  glossaryOpen.value = false;
  advancedOpen.value = false;
  section.value = at;
  snapshot.value = formState();
  settingsOriginal.value = settingsText.value;
}

/** What the main form holds, as one string, to tell whether it changed. */
function formState(): string {
  return JSON.stringify([
    editing.value,
    aclScopeText.value,
    contentScopeText.value,
    settingsText.value,
    [...glossaryIncludes.value].sort(),
    accessListed.value,
  ]);
}
const snapshot = ref("");
const settingsOriginal = ref("{}");
const mainDirty = computed(
  () => editing.value !== null && formState() !== snapshot.value,
);

function cancelEdit() {
  editing.value = null;
  aclScopeText.value = "";
  contentScopeText.value = "";
  settingsText.value = "";
  glossaryIncludes.value = new Set();
  accessListed.value = false;
}

/** RefineAssistant applied/rolled back a change — refresh the list and
 * mirror the fresh prompt into the open form so Save doesn't overwrite
 * the SME change with a stale textarea. */
async function onRefineApplied(): Promise<void> {
  await reload();
  const fresh = rows.value.find((r) => r.slug === editing.value?.slug);
  if (fresh && editing.value) editing.value.system_prompt = fresh.system_prompt;
}

/** Settings the platform owns, not the form (seed.NON_BEHAVIOURAL_SETTINGS). */
const GOVERNANCE_KEYS = new Set([
  "governance",
  "approval",
  "access_attestation",
  "access_attestations",
  "stewardship",
]);

function ownerFields(row: AssistantRow): Record<string, unknown> {
  const r = row as AssistantRow & Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of ["owner_oid", "co_owner_oids", "pilot_users"])
    if (k in r) out[k] = r[k];
  return out;
}

/** One Save for the whole editor: compliance first (its own endpoint),
 *  then the assistant itself if that changed too. */
async function saveAll(): Promise<void> {
  if (!editing.value || saving.value) return;
  if (complianceDirty.value) {
    if (!(await compliance.value?.save())) return; // cancelled or failed
    await refreshRows();
  }
  if (mainDirty.value || isNew.value) await save();
  else editing.value = null;
}

async function save() {
  if (!editing.value) return;
  saving.value = true;
  try {
    let parsedSettings: Record<string, unknown>;
    try {
      const parsed = JSON.parse(settingsText.value || "{}");
      if (
        typeof parsed !== "object" ||
        Array.isArray(parsed) ||
        parsed === null
      ) {
        throw new Error("settings must be a JSON object");
      }
      parsedSettings = parsed as Record<string, unknown>;
    } catch (e) {
      toast.error("Invalid JSON in settings", (e as Error).message);
      saving.value = false;
      return;
    }
    const acl = aclScopeText.value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const contentScope = contentScopeText.value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    // ADR 0026 v2 — sync the multi-select state back into
    // settings.glossary_includes BEFORE saving, so the picker wins
    // over whatever the JSON-edit shows. Operators can still bypass
    // the picker by editing the raw JSON if they need a slug the
    // library doesn't have (e.g. one they're about to create).
    const finalSettings: Record<string, unknown> = { ...parsedSettings };
    const incl = Array.from(glossaryIncludes.value).sort();
    if (incl.length > 0) {
      finalSettings.glossary_includes = incl;
    } else {
      delete finalSettings.glossary_includes;
    }
    if (accessListed.value) finalSettings.access_mode = "listed";
    else delete finalSettings.access_mode;
    // Three-way merge with what the server holds now. The Compliance
    // section writes to the same row (guardrails in `settings`, the owner
    // fields) through its own endpoint; a key this form did not touch is
    // taken from the fresh row, so saving here never undoes a compliance
    // edit made a minute ago in the same drawer. The governance record
    // (`governance`, `approval`, …) is left out entirely when untouched —
    // the backend carries it across.
    const fresh = rows.value.find((r) => r.slug === editing.value?.slug);
    let original: Record<string, unknown> = {};
    try {
      original = JSON.parse(settingsOriginal.value || "{}");
    } catch {
      original = {};
    }
    if (fresh) {
      const freshSettings = (fresh.settings ?? {}) as Record<string, unknown>;
      for (const k of new Set([
        ...Object.keys(finalSettings),
        ...Object.keys(original),
        ...Object.keys(freshSettings),
      ])) {
        if (k === "glossary_includes") continue;
        const untouched =
          JSON.stringify(finalSettings[k]) === JSON.stringify(original[k]);
        if (!untouched) continue;
        if (GOVERNANCE_KEYS.has(k)) delete finalSettings[k];
        else if (k in freshSettings) finalSettings[k] = freshSettings[k];
        else delete finalSettings[k];
      }
    }
    const body: AssistantRow = {
      ...editing.value,
      // Owners are set in the Compliance section; never from a stale copy.
      ...(fresh ? ownerFields(fresh) : {}),
      acl_scope: acl,
      content_scope: contentScope,
      settings: finalSettings,
    };
    const wasNew = !rows.value.some((r) => r.slug === body.slug);
    await upsertAssistant(body, props.devUser);
    await reload();
    toast.success(
      wasNew
        ? `Assistant ${body.slug} created`
        : `Assistant ${body.slug} saved`,
    );
    editing.value = null;
  } catch (e) {
    _toastError(e, "Could not save assistant");
  } finally {
    saving.value = false;
  }
}

function setBusy(slug: string, on: boolean): void {
  const next = new Set(busySlugs.value);
  if (on) next.add(slug);
  else next.delete(slug);
  busySlugs.value = next;
}

async function disable(row: AssistantRow) {
  if (busySlugs.value.has(row.slug)) return;
  const ok = await confirm({
    title: "Disable assistant?",
    message: `Disable assistant "${row.slug}"? It can be re-enabled later.`,
    confirmLabel: "Disable",
    danger: true,
  });
  if (!ok) return;
  setBusy(row.slug, true);
  try {
    await disableAssistant(row.slug, props.devUser);
    await reload();
    toast.success(`Assistant ${row.slug} disabled`);
  } catch (e) {
    _toastError(e, "Could not disable assistant");
  } finally {
    setBusy(row.slug, false);
  }
}

async function enable(row: AssistantRow) {
  if (busySlugs.value.has(row.slug)) return;
  setBusy(row.slug, true);
  try {
    // Re-enable via upsert — there's no dedicated enable endpoint; flip
    // the flag on the existing row and PUT it back.
    await upsertAssistant({ ...row, enabled: true }, props.devUser);
    await reload();
    toast.success(`Assistant ${row.slug} enabled`);
  } catch (e) {
    _toastError(e, "Could not enable assistant");
  } finally {
    setBusy(row.slug, false);
  }
}

const isNew = computed(
  () =>
    editing.value !== null &&
    !rows.value.some((r) => r.slug === editing.value!.slug),
);
</script>

<template>
  <div class="admin-assistants">
    <header class="admin-assistants__header">
      <h2>Assistants</h2>
      <input
        v-model="query"
        type="search"
        class="admin-assistants__filter"
        placeholder="Filter by name, slug or group…"
        aria-label="Filter assistants"
      />
      <span class="admin-assistants__count">
        {{ filtered.length
        }}<template v-if="filtered.length !== rows.length">
          of {{ rows.length }}</template
        >
      </span>
      <button v-write class="btn btn--primary" @click="startNew">
        + New assistant
      </button>
    </header>

    <table v-if="!loading" class="admin-table">
      <thead>
        <tr>
          <th>Assistant</th>
          <th>Who can use it</th>
          <th>What it reads</th>
          <th title="Confirm · Test · Accept">Approval</th>
          <th>Status</th>
          <th class="admin-table__actions"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="r in filtered"
          :key="r.slug"
          :class="{
            'row--disabled': !r.enabled,
            'row--editing': editing?.slug === r.slug,
          }"
        >
          <td>
            <div class="name">{{ r.name || r.slug }}</div>
            <div class="muted small mono">{{ r.slug }}</div>
          </td>
          <td class="admin-table__acl">
            <template v-if="(r.settings as Record<string, unknown>)?.access_mode === 'listed'">
              <span class="muted small">only its teams and named people</span>
            </template>
            <template v-else>
              <span v-if="r.acl_scope.length === 0" class="muted small"
                >everyone who can sign in</span
              >
              <span v-for="g in r.acl_scope" :key="g" class="pill">{{
                groupLabel(g)
              }}</span>
            </template>
            <template v-if="r.acl_scope.length || (r.settings as Record<string, unknown>)?.access_mode === 'listed'">
              <span
                v-for="t in teamAccess.get(r.slug) ?? []"
                :key="`t:${t}`"
                class="pill pill--team"
                >{{ t }}</span
              >
            </template>
            <div v-if="teamMadeFor.get(r.slug)?.length" class="muted small">
              made for {{ teamMadeFor.get(r.slug)!.join(", ") }}
            </div>
          </td>
          <td class="admin-table__acl">
            <template v-if="reads[r.slug]?.reads?.length">
              <span
                v-for="x in reads[r.slug]?.reads ?? []"
                :key="x.title"
                class="pill pill--reads"
                >{{ x.title }}</span
              >
            </template>
            <template v-else>
              <span
                v-if="!r.content_scope || r.content_scope.length === 0"
                class="muted small"
                >everything its users may see</span
              >
              <ScopeTagPill
                v-for="g in r.content_scope ?? []"
                :key="g"
                :tag="g"
                :info="scopeTags.get(g)"
              />
            </template>
          </td>
          <td>
            <button
              v-if="govBySlug.has(r.slug)"
              class="linkish"
              @click="startEdit(r, 'compliance')"
            >
              <span
                class="marks"
                :title="
                  steps(r.slug)
                    .map((st) => `${MARK[st.status]} ${st.label}`)
                    .join('\n')
                "
              >
                <span
                  v-for="st in steps(r.slug)"
                  :key="st.key"
                  :class="['mark', `mark--${st.status}`]"
                  >{{ MARK[st.status] }}</span
                >
              </span>
            </button>
            <span v-else class="muted">—</span>
          </td>
          <td>
            <span :class="['status', r.enabled ? 'status--on' : 'status--off']">
              {{ r.enabled ? (r.maturity ?? "enabled") : "disabled" }}
            </span>
          </td>
          <!-- Edit is the action you came for, so it is the visible one.
               Disable was a red outlined button on every single row — a
               column of alarm down the page, with the destructive option
               louder than the primary one. -->
          <td class="admin-table__actions">
            <button class="btn" @click="startEdit(r)">Edit</button>
            <button
              v-if="r.enabled"
              class="btn btn--ghost"
              :disabled="busySlugs.has(r.slug)"
              v-write
              @click="disable(r)"
            >
              {{ busySlugs.has(r.slug) ? "Disabling…" : "Disable" }}
            </button>
            <button
              v-else
              class="btn btn--ghost"
              :disabled="busySlugs.has(r.slug)"
              v-write
              @click="enable(r)"
            >
              {{ busySlugs.has(r.slug) ? "Enabling…" : "Enable" }}
            </button>
          </td>
        </tr>
        <tr v-if="filtered.length === 0">
          <td colspan="6" class="muted">
            <template v-if="rows.length === 0">
              No assistants yet — click "+ New assistant".
            </template>
            <template v-else>No assistant matches “{{ query }}”.</template>
          </td>
        </tr>
      </tbody>
    </table>
    <SkeletonRows v-else />

    <!-- The form used to render here, below the table: on a 15-row list that
         put it ~1400px under the fold with no scroll and no signal. It now
         opens in a drawer beside the list. -->
    <SidePanel
      v-model="editorOpen"
      :title="isNew ? 'New assistant' : editing?.name || editing?.slug || ''"
      :subtitle="isNew ? '' : (editing?.slug ?? '')"
    >
      <div v-if="editing" class="editor">
        <nav v-if="!isNew" class="sections" aria-label="Editor sections">
          <button
            v-for="x in SECTIONS"
            :key="x.id"
            type="button"
            :class="['sections__tab', section === x.id && 'sections__tab--on']"
            :aria-current="section === x.id ? 'true' : undefined"
            @click="section = x.id"
          >
            {{ x.label }}
            <span
              v-if="x.id === 'compliance' && openItems(editing.slug)"
              class="sections__count"
              >{{ openItems(editing.slug) }}</span
            >
          </button>
        </nav>

        <div v-show="isNew || section === 'setup'" class="section">
          <!-- Three tiers, because fourteen fields in a row gave the raw JSON
             settings the same weight as the name. What someone changes most
             weeks is on top; who can use it is one section; everything that
             is set once when an assistant is built folds away, with a line
             saying what it is currently set to. A NEW assistant opens the
             fold: its slug has to be chosen and cannot be changed later. -->
          <label v-if="isNew">
            Slug
            <input
              v-model="editing.slug"
              :disabled="!isNew"
              placeholder="compliance"
              pattern="[a-z0-9](?:[a-z0-9-]*[a-z0-9])?"
            />
          </label>
          <label>
            Display name
            <input v-model="editing.name" placeholder="Compliance GPT" />
          </label>
          <label>
            Description
            <input v-model="editing.description" />
          </label>
          <label>
            System prompt
            <textarea v-model="editing.system_prompt" rows="6" />
          </label>

          <section v-if="!isNew" class="fold">
            <button
              type="button"
              class="fold__bar"
              :aria-expanded="refineOpen"
              @click="refineOpen = !refineOpen"
            >
              <span class="fold__title">Improve the prompt with AI</span>
              <span class="fold__chev" aria-hidden="true">{{
                refineOpen ? "▾" : "▸"
              }}</span>
            </button>
            <div v-if="refineOpen" class="fold__body">
              <RefineAssistant
                :key="editing.slug"
                :slug="editing.slug"
                :assistant-name="editing.name"
                :dev-user="devUser"
                @applied="onRefineApplied"
              />
            </div>
          </section>
          <label>
            Release stage
            <span class="hint">
              Experimental: admins only. Pilot: named pilot users and people
              added above. Production: everyone its groups or the People list
              admit.
            </span>
            <select v-model="editing.maturity">
              <option value="experimental">Experimental</option>
              <option value="pilot">Pilot</option>
              <option value="production">Production</option>
            </select>
          </label>
          <label class="checkbox">
            <input type="checkbox" v-model="editing.enabled" />
            Enabled
          </label>

          <section class="fold">
            <button
              type="button"
              class="fold__bar"
              :aria-expanded="glossaryOpen"
              @click="glossaryOpen = !glossaryOpen"
            >
              <span class="fold__title">Glossary</span>
              <span class="fold__summary">{{
                glossaryIncludes.size
                  ? `${glossaryIncludes.size} included`
                  : "none included"
              }}</span>
              <span class="fold__chev" aria-hidden="true">{{
                glossaryOpen ? "▾" : "▸"
              }}</span>
            </button>
            <div v-if="glossaryOpen" class="fold__body">
              <label>
                Glossary includes
                <span class="hint">
                  Shared business-context definitions injected above this
                  assistant's system prompt on every turn. Manage entries in the
                  <a href="#/admin?tab=glossary">Glossary</a>
                  tab. Saves through into
                  <code>settings.glossary_includes</code>.
                </span>
                <p v-if="glossaryEntries.length === 0" class="muted">
                  No glossary entries yet — open the Glossary tab to create
                  some.
                </p>
                <div v-else class="chips">
                  <label
                    v-for="g in glossaryEntries"
                    :key="g.slug"
                    class="chip"
                    :class="{ 'chip--on': glossaryIncludes.has(g.slug) }"
                    :title="g.title || g.slug"
                  >
                    <input
                      type="checkbox"
                      :checked="glossaryIncludes.has(g.slug)"
                      @change="toggleGlossaryInclude(g.slug)"
                    />
                    <code>{{ g.slug }}</code>
                    <span v-if="g.title" class="chip__title"
                      >— {{ g.title }}</span
                    >
                  </label>
                </div>
              </label>
            </div>
          </section>

          <section class="fold">
            <button
              type="button"
              class="fold__bar"
              :aria-expanded="advancedOpen"
              @click="advancedOpen = !advancedOpen"
            >
              <span class="fold__title">Advanced</span>
              <span class="fold__summary">{{ advancedSummary }}</span>
              <span class="fold__chev" aria-hidden="true">{{
                advancedOpen ? "▾" : "▸"
              }}</span>
            </button>
            <div v-if="advancedOpen" class="fold__body">
              <label v-if="!isNew">
                Slug
                <input
                  v-model="editing.slug"
                  :disabled="!isNew"
                  placeholder="compliance"
                  pattern="[a-z0-9](?:[a-z0-9-]*[a-z0-9])?"
                />
              </label>
              <label>
                Content scope — corpus partition (ADR 0010)
                <input
                  v-model="contentScopeText"
                  placeholder="compliance, iso-37001"
                />
                <span class="hint">
                  Comma-separated <code>scope_tags</code>. Only chunks tagged
                  with any of these flow through retrieval. Empty = unscoped
                  (assistant retrieves from every chunk it has ACL for).
                </span>
              </label>
              <label>
                Chat deployment override (blank → backend default)
                <input
                  v-model="editing.chat_deployment"
                  placeholder="blank → use backend default"
                />
              </label>
              <label class="inline-check">
                <input type="checkbox" v-model="editing.dedicated_index" />
                <span>
                  Dedicated index
                  <span class="hint">
                    Use
                    <code
                      >choir-content-{{ editing.slug || "&lt;slug&gt;" }}</code
                    >
                    instead of the shared index. Flip on for very large or
                    strictly-isolated corpora.
                  </span>
                </span>
              </label>
              <label>
                Settings (JSON object)
                <span class="hint">
                  Free-form settings the chat path reads. The Glossary includes
                  picker above overrides
                  <code>settings.glossary_includes</code> on save; other keys
                  are preserved verbatim.
                </span>
                <textarea v-model="settingsText" rows="5" class="mono" />
              </label>
            </div>
          </section>
        </div>

        <section v-show="isNew || section === 'access'" class="tier">
          <h4>Who can use it</h4>
          <AssistantMembers
            v-if="!isNew"
            :key="editing.slug"
            :slug="editing.slug"
            :dev-user="devUser"
          />
          <p v-if="isNew" class="muted">
            Save first — then people can be added here by e-mail.
          </p>
          <fieldset class="access-mode">
            <legend>Who gets in</legend>
            <label class="radio">
              <input v-model="accessListed" type="radio" :value="false" />
              <span>
                <strong>Everyone in its groups</strong>, plus its teams and the
                people named here
              </span>
            </label>
            <label class="radio">
              <input v-model="accessListed" type="radio" :value="true" />
              <span>
                <strong>Only its teams and the people named here</strong>
                (and its owners). Being in a group is not enough; others do
                not see it on their Hub.
              </span>
            </label>
          </fieldset>
          <label>
            {{ accessListed ? "Groups its members carry inside it" : "Groups that can open it (optional)" }}
            <input
              v-model="aclScopeText"
              placeholder="group:compliance, group:legal"
            />
            <span class="hint">
              <template v-if="accessListed">
                These no longer let anyone in. A team member or named person
                acts as a member of these groups inside this assistant — which
                is what its action permissions check.
              </template>
              <template v-else>
                Comma-separated. Empty means everyone who can sign in — then the
                release stage and the People list are what limit it.
              </template>
            </span>
          </label>
          <div class="teams-line">
            <strong>Teams:</strong>
            <template
              v-if="
                (teamAccess.get(editing.slug) ?? []).length ||
                (teamMadeFor.get(editing.slug) ?? []).length
              "
            >
              <span v-if="(teamAccess.get(editing.slug) ?? []).length">
                may use — {{ teamAccess.get(editing.slug)!.join(", ") }}.
              </span>
              <span v-if="(teamMadeFor.get(editing.slug) ?? []).length">
                Made for — {{ teamMadeFor.get(editing.slug)!.join(", ") }}.
              </span>
            </template>
            <span v-else class="muted">none.</span>
            <button type="button" class="link" @click="openTeams">
              Manage teams
            </button>
          </div>
          <template v-if="govBySlug.get(editing.slug)">
            <h4>How access is decided</h4>
            <AssistantAccessReview
              :entry="govBySlug.get(editing.slug)!"
              :dev-user="devUser"
              @updated="onGovUpdated"
            />
          </template>
          <template v-if="!isNew">
            <h4>Check it: access tests</h4>
            <AccessTests
              :key="editing.slug"
              :slug="editing.slug"
              :dev-user="devUser"
              @changed="loadGov"
            />
          </template>
        </section>

        <section v-if="!isNew" v-show="section === 'reads'" class="tier">
          <h4>What it reads</h4>
          <AssistantReads
            :key="editing.slug"
            :slug="editing.slug"
            :dev-user="devUser"
          />
        </section>

        <AssistantCompliance
          v-if="!isNew && gov && govBySlug.get(editing.slug)"
          v-show="section === 'compliance'"
          ref="compliance"
          :key="editing.slug"
          :entry="govBySlug.get(editing.slug)!"
          :controls="gov.controls"
          :deployment="gov.deployment"
          :window-days="gov.window_days"
          :dev-user="devUser"
          @updated="onGovUpdated"
        />
        <p v-else-if="!isNew && section === 'compliance'" class="muted small">
          The compliance record could not be loaded.
        </p>
      </div>
      <template #actions>
        <span
          v-if="!isNew && (mainDirty || complianceDirty)"
          class="muted small unsaved"
          >Unsaved changes<template v-if="mainDirty && complianceDirty">
            in two sections</template
          ></span
        >
        <button class="btn" :disabled="saving" @click="cancelEdit">
          {{ mainDirty || complianceDirty ? "Cancel" : "Close" }}
        </button>
        <button
          class="btn btn--primary"
          :disabled="saving || (!isNew && !mainDirty && !complianceDirty)"
          v-write
          @click="saveAll"
        >
          {{ saving ? "Saving…" : "Save" }}
        </button>
      </template>
    </SidePanel>
  </div>
</template>

<style scoped>
.marks {
  display: inline-flex;
  gap: 0.2rem;
}
.mark {
  display: inline-flex;
  width: 1.25rem;
  height: 1.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 700;
}
.mark--ok {
  color: var(--color-surface);
  background: var(--color-success);
}
.mark--todo {
  color: var(--color-error);
  border: 1.5px solid var(--color-error);
}
.mark--na {
  color: var(--color-muted);
}
.access-mode {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 0.5rem 0.75rem 0.6rem;
  margin: 0 0 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.access-mode legend {
  font-size: 0.8rem;
  color: var(--color-muted);
  padding: 0 0.25rem;
}
.access-mode .radio {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  font-weight: 400;
}
.tier {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-border);
}
.tier h4 {
  margin: 0;
}
.fold {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
.fold__bar {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  width: 100%;
  padding: 0.5rem 0.7rem;
  font: inherit;
  color: inherit;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
}
.fold__title {
  font-weight: 600;
}
.fold__summary {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82rem;
  color: var(--color-muted);
}
/* No summary between them: the chevron still belongs on the right. */
.fold__title + .fold__chev {
  margin-left: auto;
}
.fold__chev {
  color: var(--color-muted);
}
.fold__body {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: 0 0.7rem 0.7rem;
}
.admin-assistants {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.admin-assistants__header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  /* A title, a search box, a count and a primary button in one unwrapping
     row is wider than a phone. Wrapping is the floor; the media query below
     then gives each part somewhere sensible to go. */
  flex-wrap: wrap;
}
.admin-assistants__header h2 {
  margin: 0;
  margin-right: auto;
}
.admin-assistants__filter {
  width: min(20rem, 40vw);
  padding: 0.42rem 0.85rem;
  border-radius: var(--radius-pill);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-ink);
  font-size: 0.85rem;
}

/* Phones: the heading takes its own line, the filter absorbs whatever width
   is left (min-width:0 is what actually lets a flex item shrink below its
   content), and the button keeps its size and wraps rather than being
   squashed to an unreadable sliver. `40vw` was the specific problem — on a
   390px screen that is 156px of search box wedged between three other
   things. */
@media (max-width: 640px) {
  .admin-assistants__header h2 {
    flex: 1 0 100%;
    margin-right: 0;
  }
  .admin-assistants__filter {
    flex: 1 1 10rem;
    width: auto;
    min-width: 0;
  }
  .admin-assistants__header .btn {
    flex: 0 0 auto;
    white-space: nowrap;
  }
}
.admin-assistants__filter::placeholder {
  color: var(--color-muted);
}
.admin-assistants__count {
  font-size: 0.8rem;
  color: var(--color-muted);
  font-variant-numeric: tabular-nums;
}
/* The drawer covers the right of the screen; this keeps the row you are
   editing identifiable in the part of the list still visible. */
.admin-table tbody tr.row--editing {
  background: color-mix(in srgb, var(--color-accent) 8%, transparent);
  box-shadow: inset 3px 0 0 var(--color-accent);
}
/* Edit is the action; Disable/Enable is the exception. Two buttons on every
   row put 32 of them on a 15-row screen, which is a column of noise beside
   the data people came to read. The secondary one fades in on hover — and on
   keyboard focus, so it is never focusable while invisible. `visibility` is
   deliberately not used: the row must not reflow when it appears. */
.admin-table__actions .btn--ghost {
  opacity: 0;
  transition: opacity var(--dur-1, 120ms) var(--ease-out, ease);
}
tr:hover .admin-table__actions .btn--ghost,
tr:focus-within .admin-table__actions .btn--ghost {
  opacity: 1;
}
@media (hover: none) {
  /* Touch has no hover to reveal it with. */
  .admin-table__actions .btn--ghost {
    opacity: 1;
  }
}
.admin-table__actions {
  text-align: right;
  white-space: nowrap;
}
/* `--color-bg-alt` and `--color-fg` are not tokens in theme.css — these were
   always falling through to a hardcoded `#eef` with an unset text colour, so
   the pills ignored the tenant palette entirely. */
.admin-table__acl .pill {
  display: inline-block;
  padding: 0.1rem 0.5rem;
  margin: 0 0.25rem 0.2rem 0;
  font-family: var(--font-mono, monospace);
  font-size: 0.78rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-ink) 6%, transparent);
  color: var(--color-muted);
}
.admin-table__acl .pill--scope {
  background: var(--color-accent-dim);
  color: var(--color-accent);
}
.admin-table__acl .pill--accent {
  background: var(--color-accent);
  color: white;
}
.hint {
  display: block;
  font-size: 0.8rem;
  font-weight: 400;
  color: var(--color-muted);
  margin-top: 2px;
  line-height: 1.4;
}
.editor .inline-check {
  flex-direction: row;
  align-items: flex-start;
  gap: 0.5rem;
}
.editor .inline-check input[type="checkbox"] {
  margin-top: 4px;
  width: auto;
}
.editor .inline-check > span {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-weight: 500;
  color: var(--color-ink);
}
.muted {
  color: var(--color-muted);
}
.status--on {
  color: var(--color-success);
  font-weight: 600;
}
.status--off {
  color: var(--color-muted);
  font-style: italic;
}
.error {
  color: var(--color-error);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-md);
}
.editor {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: var(--space-3);
  padding: var(--space-3);
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
.editor label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.editor input,
.editor textarea {
  font-size: 0.95rem;
  font-family: inherit;
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
}
.editor textarea.mono {
  font-family: var(--font-mono, monospace);
}
.editor .checkbox {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
}
/* ADR 0026 v2 — glossary include picker */
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.4rem;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  cursor: pointer;
  user-select: none;
  background: var(--color-surface, transparent);
}
.chip input[type="checkbox"] {
  margin: 0;
}
.chip--on {
  border-color: var(--color-accent);
  background: color-mix(in srgb, var(--color-accent) 12%, transparent);
}
.chip__title {
  color: var(--color-muted);
  font-size: 0.85rem;
}
.hint {
  display: block;
  color: var(--color-muted);
  font-size: 0.85rem;
  margin-top: 0.2rem;
  margin-bottom: 0.3rem;
}
.muted {
  color: var(--color-muted);
  font-size: 0.9rem;
}
.editor__actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
}
.admin-table__actions .btn {
  margin-left: 0.4rem;
}
/* Dim disabled assistant rows so they read as inactive at a glance.
   Opacity on the row doesn't block pointer events, so the Enable
   button stays fully clickable. */
.row--disabled {
  opacity: 0.6;
}

/* ADR 0038 — SME refine panel */
.refine {
  border: 1px solid var(--color-border);
  border-left: 3px solid #279db6;
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  margin: 0.75rem 0;
}
.refine h4 {
  margin: 0 0 0.25rem;
}
.name {
  font-weight: 600;
}
.small {
  font-size: 0.8rem;
}
.mono {
  font-family: var(--font-mono, monospace);
}
/* Sources are names, not identifiers: set them as text, not as code. */
.admin-table__acl .pill--reads {
  font-family: inherit;
  text-transform: none;
  letter-spacing: normal;
  background: color-mix(in srgb, var(--color-accent) 10%, transparent);
}
button.linkish {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.unsaved {
  margin-right: auto;
}
/* The editor's four sections: one strip, the current one underlined. */
.sections {
  display: flex;
  gap: 0.25rem;
  border-bottom: 1px solid var(--color-border);
  margin: -0.25rem 0 0.25rem;
  overflow-x: auto;
}
.sections__tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 0.45rem 0.7rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-muted);
  cursor: pointer;
  white-space: nowrap;
}
.sections__tab--on {
  color: var(--color-ink);
  border-bottom-color: var(--color-accent);
  font-weight: 600;
}
.sections__count {
  margin-left: 0.3rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  font-size: 0.72rem;
  background: color-mix(in srgb, var(--color-warn) 18%, transparent);
  color: var(--color-warn-ink);
}
.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.admin-table__acl .pill--team {
  font-family: inherit;
  background: color-mix(in srgb, var(--color-accent) 14%, transparent);
}
.teams-line {
  font-size: 0.86rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: baseline;
}
.teams-line .link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--color-accent);
  cursor: pointer;
}
</style>
